// Full-document render of the customer shipment-tracking page: the
// breadcrumbs-block, the shared order-tracker island (unified order + pipeline
// config + review link), and the CMS `tracking_page_blocks` at the bottom.
import { Immutable } from "immer";
import React from "react";
import { FAQsBlock } from "tsi-common-react/src/components/FAQsBlock/index";
import { ProductCalloutBlock } from "tsi-common-react/src/components/ProductCalloutBlock/index";

import {
    SharedFAQsBlock as FAQsBlockValue,
    ProductCalloutBlock as ProductCalloutBlockValue,
    templates,
} from "@reactivated";

import { PageSkeleton } from "../components/PageSkeleton";
import { Template as TrackOrderDetails } from "./TrackOrderDetailsTemplate";

// tracking_page_blocks is an inline Wagtail StreamField (not a thelabui client-
// renderable field), so it cannot be Pick'd through reactivated. The server
// serializes each block to a {type, id, value} row (value = the block's
// client-render data, matching the shared component's canonical value type) and
// the wrapper dispatches on type.
type TrackingBlockRow =
    | {
          readonly type: "faqs";
          readonly id: string;
          readonly value: FAQsBlockValue;
      }
    | {
          readonly type: "product_callout";
          readonly id: string;
          readonly value: ProductCalloutBlockValue;
      };

// Each block renders its component directly, with no `w-block-{type}` wrapper
// div: the blocks render per-block (not as one whole-StreamField value), which
// is what emits those wrappers.
const renderTrackingBlock = (
    block: Immutable<TrackingBlockRow>,
): React.ReactNode => {
    switch (block.type) {
        case "faqs":
            return <FAQsBlock key={block.id} value={block.value} />;
        case "product_callout":
            return <ProductCalloutBlock key={block.id} value={block.value} />;
    }
};

export const Template = (
    props: templates.TrackShipmentDetailsDocumentTemplate,
): React.JSX.Element => {
    const trackingBlocks: readonly Immutable<TrackingBlockRow>[] =
        props.tracking_blocks_json
            ? (JSON.parse(props.tracking_blocks_json) as Immutable<
                  TrackingBlockRow[]
              >)
            : [];
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            {/* React 19 hoists this <meta> into <head>; the description meta
                renders from page_chrome.search_description. */}
            <meta name="chatTags" content="trackShipment" />
            <div className="l-capped-width u-margin-bottom--xl u-full-width-title order-list track-shipment-details">
                <section className="breadcrumbs-block">
                    {props.crumbs.map((crumb, i) => (
                        <React.Fragment key={i}>
                            {i > 0 && " / "}
                            {crumb.href ? (
                                <a href={crumb.href}>{crumb.label}</a>
                            ) : (
                                crumb.label
                            )}
                        </React.Fragment>
                    ))}
                </section>
                <TrackOrderDetails {...props.track_order_details} />
            </div>
            <section className="track-shipment-blocks">
                {trackingBlocks.map((block) => renderTrackingBlock(block))}
            </section>
        </PageSkeleton>
    );
};
